import { useTranslation } from 'react-i18next';
import { sorteerbaar, type Kolom, type Richting, type Sortering } from '@xxtract/tabelfilters';
import { FOCUS_CLASS } from './stijl';

const PIJL_ICONS: Record<Richting, string> = { op: 'arrow_upward', af: 'arrow_downward' };
export const RICHTING_KEYS: Record<Richting, string> = { op: 'tabel.oplopend', af: 'tabel.aflopend' };

/** `aria-sort` voor een `th` of `TreeTableColumn`: alleen de eerste sorteerkolom (ARIA staat er één toe, §5). */
export function ariaSort(sortering: Sortering, sleutel: string): 'ascending' | 'descending' | undefined {
  const eerste = sortering[0];
  if (eerste?.sleutel !== sleutel) return undefined;
  return eerste.richting === 'op' ? 'ascending' : 'descending';
}

interface SorteerbareKopProps {
  /** Alleen de beschrijving van de kolom; `never` laat elke rijvorm toe. */
  kolom: Kolom<never, never>;
  sortering: Sortering;
  onKlik: () => void;
  /** Standaardsortering: bij een lege reeks een gedempte pijl op die kop. */
  standaard?: Sortering;
  /** `maxSorteerkolommen` van de lijst; bij 1 zegt de kop dat (orders). */
  max?: number;
  /** De bron legt de volgorde op: geen knop. */
  vergrendeld?: boolean;
}

/** Kolomkop die sorteert met een klik: pijl, richting en bij twee of meer kolommen een volgnummer (CAP-5, CAP-15). */
export function SorteerbareKop({ kolom, sortering, onKlik, standaard, max, vergrendeld }: SorteerbareKopProps) {
  const { t } = useTranslation();
  const kop = t(kolom.kopSleutel);
  if (vergrendeld || !sorteerbaar(kolom)) return <span>{kop}</span>;

  const index = sortering.findIndex((s) => s.sleutel === kolom.sleutel);
  const actief = index >= 0 ? sortering[index] : undefined;
  const gedempt = sortering.length === 0 ? standaard?.find((s) => s.sleutel === kolom.sleutel) : undefined;
  const richting = actief?.richting ?? gedempt?.richting;

  return (
    <button
      // Niet sorterend: de naam zegt wat de klik doet, zodat hij niet samenvalt met de filterknop van dezelfde kolom.
      aria-label={
        actief
          ? t('tabel.sorteerKopNaam', { kolom: kop, nummer: index + 1, richting: t(RICHTING_KEYS[actief.richting]) })
          : `${kop}, ${t(max === 1 ? 'tabel.eenSorteerkolom' : 'tabel.sorteringToevoegen')}`
      }
      className={`inline-flex min-h-11 items-center gap-1 rounded-[var(--radius-control)] text-left [text-transform:inherit] hover:text-[var(--foreground)] md:min-h-0 ${FOCUS_CLASS}`}
      onClick={onKlik}
      title={max === 1 ? t('tabel.eenSorteerkolom') : undefined}
      type="button"
    >
      <span>{kop}</span>
      {richting ? (
        <span
          aria-hidden="true"
          className={`material-symbols-outlined text-[16px] ${gedempt ? 'text-[var(--foreground-muted)] opacity-60' : 'text-[var(--secondary)]'}`}
          data-gedempt={gedempt ? 'true' : undefined}
          data-richting={richting}
        >
          {PIJL_ICONS[richting]}
        </span>
      ) : null}
      {actief && sortering.length >= 2 ? (
        <span
          aria-hidden="true"
          className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--foreground)] px-1 text-[10px] font-semibold text-[var(--background)]"
          data-testid="sorteer-volgnummer"
        >
          {index + 1}
        </span>
      ) : null}
    </button>
  );
}
